import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Grid, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Divider,
  Chip,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Edit as EditIcon,
  Save as SaveIcon,
  MoneyOff as DiscountIcon,
  QrCode as ScanIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';

// Data interface
interface InventoryItem {
  id: string;
  sku: string;
  name: string;
  category: string;
  price: number;
  cost: number;
  margin: number;
  salePrice: number | null;
  saleActive: boolean;
  quantity: number;
  reorderPoint: number;
  location: string;
}

const PricingPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<InventoryItem[]>([]);
  const [priceDialog, setPriceDialog] = useState(false);
  const [saleDialog, setSaleDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState<InventoryItem | null>(null);

  // Categories
  const categories = ['Apparel', 'Food', 'Drinks', 'Fishing', 'Boating', 'Souvenirs'];

  useEffect(() => {
    // In a real implementation, you would fetch inventory data from your database here
    // For now, we're initializing with empty state
    setLoading(false);
  }, []);

  // Apply filters to items
  useEffect(() => {
    const filtered = items.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           item.sku.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
      
      const matchesPriceFilter = 
        priceFilter === 'all' ||
        (priceFilter === 'sale' && item.saleActive) ||
        (priceFilter === 'low-margin' && item.margin < 30) ||
        (priceFilter === 'high-margin' && item.margin > 50);
      
      return matchesSearch && matchesCategory && matchesPriceFilter;
    });
    
    setFilteredItems(filtered);
  }, [searchTerm, categoryFilter, priceFilter, items]);

  // Handle opening the update price dialog
  const handleEditPrice = (item: InventoryItem) => {
    setCurrentItem({...item});
    setPriceDialog(true);
  };

  // Handle opening the sale dialog
  const handleEditSale = (item: InventoryItem) => {
    setCurrentItem({...item});
    setSaleDialog(true);
  };

  // Save updated regular price
  const handleSavePrice = () => {
    if (currentItem) {
      // Calculate new margin
      const newMargin = parseFloat(((currentItem.price - currentItem.cost) / currentItem.price * 100).toFixed(1));
      
      const updatedItem = {
        ...currentItem,
        margin: newMargin
      };
      
      // Update item in state
      setItems(prev => prev.map(item => 
        item.id === updatedItem.id ? updatedItem : item
      ));
      
      setPriceDialog(false);
    }
  };

  // Save updated sale price
  const handleSaveSale = () => {
    if (currentItem) {
      // Update item in state
      setItems(prev => prev.map(item => 
        item.id === currentItem.id ? currentItem : item
      ));
      
      setSaleDialog(false);
    }
  };

  // Toggle sale active status
  const handleToggleSale = (item: InventoryItem) => {
    const updatedItem = {
      ...item,
      saleActive: !item.saleActive
    };
    
    // If enabling a sale but no sale price exists, set a default discount
    if (updatedItem.saleActive && !updatedItem.salePrice) {
      updatedItem.salePrice = parseFloat((item.price * 0.9).toFixed(2));
    }
    
    // Update item in state
    setItems(prev => prev.map(i => 
      i.id === updatedItem.id ? updatedItem : i
    ));
  };

  // Render mobile card view
  const renderMobileView = () => (
    <Grid container spacing={2}>
      {filteredItems.length === 0 ? (
        <Grid item xs={12}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography>No items found. Add inventory items first.</Typography>
          </Paper>
        </Grid>
      ) : (
        filteredItems.map((item) => (
          <Grid key={item.id} item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      {item.name}
                      {item.saleActive && (
                        <Chip 
                          size="small" 
                          color="secondary" 
                          label="ON SALE" 
                          icon={<DiscountIcon />} 
                          sx={{ ml: 1, verticalAlign: 'middle' }} 
                        />
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      SKU: {item.sku} | {item.category}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      Regular Price: 
                    </Typography>
                    <Typography variant="h6" color="primary" sx={{ 
                      textDecoration: item.saleActive ? 'line-through' : 'none',
                      color: item.saleActive ? 'text.secondary' : 'primary.main'
                    }}>
                      ${item.price.toFixed(2)}
                    </Typography>
                  </Grid>
                  {item.saleActive && (
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        Sale Price: 
                      </Typography>
                      <Typography variant="h6" color="secondary">
                        ${item.salePrice?.toFixed(2)}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      Cost: ${item.cost.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      Margin: {item.margin}%
                      {item.margin < 30 ? (
                        <TrendingDownIcon fontSize="small" color="error" sx={{ verticalAlign: 'middle' }} />
                      ) : item.margin > 50 ? (
                        <TrendingUpIcon fontSize="small" color="success" sx={{ verticalAlign: 'middle' }} />
                      ) : null}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
              <Divider />
              <CardActions>
                <Button size="small" onClick={() => handleEditPrice(item)}>Update Price</Button>
                <Button 
                  size="small" 
                  color={item.saleActive ? "error" : "secondary"}
                  onClick={() => item.saleActive ? handleToggleSale(item) : handleEditSale(item)}
                >
                  {item.saleActive ? "End Sale" : "Start Sale"}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))
      )}
    </Grid>
  );

  // Render desktop table view
  const renderTableView = () => (
    <TableContainer component={Paper}>
      {filteredItems.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography>No items found. Add inventory items first.</Typography>
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>SKU</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Cost</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Sale Price</TableCell>
              <TableCell align="right">Margin</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.map((item) => (
              <TableRow 
                key={item.id}
                sx={{ 
                  backgroundColor: item.saleActive ? 'rgba(76, 175, 80, 0.1)' : 'inherit'
                }}
              >
                <TableCell>{item.sku}</TableCell>
                <TableCell>
                  {item.name}
                  {item.saleActive && (
                    <Chip 
                      size="small" 
                      color="secondary" 
                      label="SALE" 
                      sx={{ ml: 1, verticalAlign: 'middle' }} 
                    />
                  )}
                </TableCell>
                <TableCell>{item.category}</TableCell>
                <TableCell align="right">${item.cost.toFixed(2)}</TableCell>
                <TableCell align="right" sx={{ 
                  textDecoration: item.saleActive ? 'line-through' : 'none',
                  color: item.saleActive ? 'text.secondary' : 'inherit'
                }}>
                  ${item.price.toFixed(2)}
                </TableCell>
                <TableCell align="right" sx={{ color: 'secondary.main' }}>
                  {item.saleActive ? `$${item.salePrice?.toFixed(2)}` : '—'}
                </TableCell>
                <TableCell align="right">
                  {item.margin}%
                  {item.margin < 30 ? (
                    <TrendingDownIcon fontSize="small" color="error" sx={{ verticalAlign: 'middle', ml: 0.5 }} />
                  ) : item.margin > 50 ? (
                    <TrendingUpIcon fontSize="small" color="success" sx={{ verticalAlign: 'middle', ml: 0.5 }} />
                  ) : null}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => handleEditPrice(item)}>
                    <EditIcon fontSize="small" />
                  </Button>
                  <Button 
                    size="small" 
                    color={item.saleActive ? "error" : "secondary"}
                    onClick={() => item.saleActive ? handleToggleSale(item) : handleEditSale(item)}
                    sx={{ ml: 1 }}
                  >
                    {item.saleActive ? "End Sale" : "Sale"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Price Management
      </Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Search Items"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map(category => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter</InputLabel>
              <Select
                value={priceFilter}
                label="Filter"
                onChange={(e) => setPriceFilter(e.target.value)}
              >
                <MenuItem value="all">All Items</MenuItem>
                <MenuItem value="sale">On Sale</MenuItem>
                <MenuItem value="low-margin">Low Margin</MenuItem>
                <MenuItem value="high-margin">High Margin</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ScanIcon />}
              onClick={() => alert('Scan functionality would open the camera to scan barcodes')}
            >
              Scan
            </Button>
          </Grid>
        </Grid>
      </Paper>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              {filteredItems.length} items found
            </Typography>
          </Box>
          
          {isMobile ? renderMobileView() : renderTableView()}
        </>
      )}
      {/* Update Price Dialog */}
      <Dialog open={priceDialog} onClose={() => setPriceDialog(false)}>
        <DialogTitle>Update Price - {currentItem?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cost"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                value={currentItem?.cost || ''}
                onChange={(e) => {
                  const newCost = parseFloat(e.target.value);
                  if (currentItem) {
                    const newMargin = parseFloat(((currentItem.price - newCost) / currentItem.price * 100).toFixed(1));
                    setCurrentItem({...currentItem, cost: newCost, margin: newMargin});
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Price"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                value={currentItem?.price || ''}
                onChange={(e) => {
                  const newPrice = parseFloat(e.target.value);
                  if (currentItem) {
                    const newMargin = parseFloat(((newPrice - currentItem.cost) / newPrice * 100).toFixed(1));
                    setCurrentItem({...currentItem, price: newPrice, margin: newMargin});
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Margin %"
                type="number"
                value={currentItem?.margin || ''}
                onChange={(e) => {
                  const newMargin = parseFloat(e.target.value);
                  if (currentItem) {
                    // Calculate new price based on margin
                    const newPrice = parseFloat((currentItem.cost / (1 - newMargin / 100)).toFixed(2));
                    setCurrentItem({...currentItem, price: newPrice, margin: newMargin});
                  }
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceDialog(false)}>Cancel</Button>
          <Button onClick={handleSavePrice} variant="contained" startIcon={<SaveIcon />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Sale Dialog */}
      <Dialog open={saleDialog} onClose={() => setSaleDialog(false)}>
        <DialogTitle>Create Sale - {currentItem?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body1" gutterBottom>
                Regular Price: ${currentItem?.price.toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Sale Price"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                value={currentItem?.salePrice || ''}
                onChange={(e) => {
                  const newSalePrice = parseFloat(e.target.value);
                  if (currentItem) {
                    setCurrentItem({...currentItem, salePrice: newSalePrice});
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Discount %"
                type="number"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                value={currentItem ? Math.round((1 - (currentItem.salePrice || 0) / currentItem.price) * 100) : ''}
                onChange={(e) => {
                  const discountPercent = parseFloat(e.target.value);
                  if (currentItem) {
                    const newSalePrice = parseFloat((currentItem.price * (1 - discountPercent / 100)).toFixed(2));
                    setCurrentItem({...currentItem, salePrice: newSalePrice});
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaleDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              if (currentItem) {
                setCurrentItem({...currentItem, saleActive: true});
                handleSaveSale();
              }
            }} 
            variant="contained" 
            color="secondary"
            startIcon={<DiscountIcon />}
          >
            Start Sale
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PricingPage; 